interface TranslationResponse {
  originalText: string;
  translatedText: string;
  fromLanguage: string;
  toLanguage: string;
  success: boolean;
  error?: string;
}

/**
 * Translate a message using the Supabase Edge Function
 */
export async function translateMessage(
  text: string, 
  fromLanguage: 'english' | 'french' | 'haitian_creole',
  toLanguage: string = 'Spanish'
): Promise<string> {
  try {
    if (!text || text.trim().length === 0) {
      throw new Error('Text is required for translation');
    }

    if (text.length > 2000) {
      throw new Error('Message too long. Maximum 2000 characters allowed.');
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!supabaseUrl) {
      throw new Error('Supabase URL not configured');
    }

    const response = await fetch(`${supabaseUrl}/functions/v1/message-translate-learning`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        text: text.trim(),
        fromLanguage,
        toLanguage
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Network error' }));
      throw new Error(errorData.error || `Translation service error: ${response.status}`);
    }

    const result: TranslationResponse = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Translation failed');
    }

    if (!result.translatedText) {
      throw new Error('No translation received');
    }

    return result.translatedText;
  } catch (error: any) {
    console.error('Translation error:', error);
    
    if (error.message.includes('fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('Failed to translate message');
    }
  }
}

/**
 * Detect the language of a text (basic heuristic)
 */
export function detectLanguage(text: string): 'english' | 'french' | 'haitian_creole' {
  const lowerText = text.toLowerCase();
  
  // Haitian Creole indicators
  const haitianWords = ['mwen', 'ou', 'li', 'nou', 'yo', 'ak', 'nan', 'pou', 'ki', 'kote', 'kijan', 'poukisa'];
  const haitianCount = haitianWords.filter(word => lowerText.includes(word)).length;
  
  // French indicators
  const frenchWords = ['je', 'tu', 'il', 'elle', 'nous', 'vous', 'ils', 'elles', 'avec', 'dans', 'pour', 'que', 'qui', 'où', 'comment', 'pourquoi'];
  const frenchCount = frenchWords.filter(word => lowerText.includes(word)).length;
  
  // English indicators
  const englishWords = ['the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by'];
  const englishCount = englishWords.filter(word => lowerText.includes(word)).length;
  
  // Return language with highest score
  if (haitianCount > frenchCount && haitianCount > englishCount) {
    return 'haitian_creole';
  } else if (frenchCount > englishCount) {
    return 'french';
  } else {
    return 'english';
  }
}

/**
 * Get language display information
 */
export function getLanguageInfo(language: string) {
  const languageInfo = {
    'english': { flag: '🇺🇸', label: 'English', nativeName: 'English' },
    'french': { flag: '🇫🇷', label: 'French', nativeName: 'Français' },
    'haitian_creole': { flag: '🇭🇹', label: 'Haitian Creole', nativeName: 'Kreyòl Ayisyen' },
    'spanish': { flag: '🇪🇸', label: 'Spanish', nativeName: 'Español' }
  };

  return languageInfo[language as keyof typeof languageInfo] || {
    flag: '🌐',
    label: language,
    nativeName: language
  };
}

/**
 * Check if translation service is available
 */
export function isTranslationServiceAvailable(): boolean {
  return !!(import.meta.env.VITE_SUPABASE_URL && import.meta.env.VITE_SUPABASE_ANON_KEY);
}

/**
 * Get supported languages
 */
export function getSupportedLanguages() {
  return [
    { value: 'english', ...getLanguageInfo('english') },
    { value: 'french', ...getLanguageInfo('french') },
    { value: 'haitian_creole', ...getLanguageInfo('haitian_creole') }
  ];
}